import { makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components';
import { AuthStatus, useAuth, useT } from '@powerportalspro/react';
import { KanbanBoard, type ViewDefinition } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import { DemoShowcaseLayout } from './DemoShowcaseLayout';
import { companiesIn, MIDWEST, NORTHEAST, SOUTHEAST, SOUTHWEST, WEST, type DemoRegion } from './DemoRegion';

// The tasks of the demo's sample data, and a signed-in visitor's own, a lane for each status reason
// from Not Started through Completed and Canceled, beside what to try on it, and a view for each region
// of the country of the tasks about companies there. The sample tasks stay where they are: visitors
// can't change them. A signed-in visitor adds up to five tasks of their own
// with the + in a lane's header, and moves them for real.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.KanbanBoardShowcase.KanbanBoardTasksMainDemo';

// Dataverse's own "All Tasks" view, which every environment has under this id.
const ALL_TASKS_VIEW_ID = '2b5f5a5d-2d23-4fe7-aa58-e77995368ae7';

// A view of the tasks about each region's companies, beside Dataverse's view of them all.
const REGION_VIEWS: { region: DemoRegion; viewId: string }[] = [
  { region: WEST, viewId: '94f61774-9e88-4446-b144-b1f8c2f52c7e' },
  { region: SOUTHWEST, viewId: '76257cc8-5aa9-469d-a0d4-0b43779f7858' },
  { region: MIDWEST, viewId: 'e9bc9054-5c3f-4078-9015-7c316ae7802e' },
  { region: SOUTHEAST, viewId: '421e23ad-10bf-4055-aa82-e65efe2a24aa' },
  { region: NORTHEAST, viewId: '1e2a62ad-b876-4f9d-9d73-454ab607c452' },
];

// Dataverse's "All Tasks" about the region's companies alone: its columns and its order.
function tasksIn(id: string, displayName: string, region: DemoRegion): ViewDefinition {
  return {
    id,
    tableName: 'task',
    displayName,
    fetchXml: `
      <fetch>
        <entity name="task">
          <attribute name="subject" />
          <attribute name="regardingobjectid" />
          <attribute name="ownerid" />
          <attribute name="prioritycode" />
          <attribute name="scheduledend" />
          <attribute name="statuscode" />
          <order attribute="subject" />
          ${companiesIn(region, 'regardingobjectid')}
        </entity>
      </fetch>`,
    columns: [
      { columnName: 'subject' },
      { columnName: 'regardingobjectid' },
      { columnName: 'ownerid' },
      { columnName: 'prioritycode' },
      { columnName: 'scheduledend' },
      { columnName: 'statuscode' },
    ],
  };
}

// Not Started, In Progress, Waiting on someone else and Deferred, which are open, then Completed and
// Canceled.
const STATUS_REASONS = ['2', '3', '4', '7', '5', '6'];

// At most 200 tasks Not Started and 30 In Progress, which the sample data already passes: the
// headers warn of a lane over its limit, and take its cards all the same.
const LANE_LIMITS = { '2': 200, '3': 30 };

const useStyles = makeStyles({
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function KanbanBoardTasksMainSample() {
  const styles = useStyles();
  const t = useT();
  const auth = useAuth();
  const signedIn = auth.status === AuthStatus.Authenticated;

  // Built once, as a new list starts the board again.
  const views = useMemo(
    () => REGION_VIEWS.map(({ region, viewId }) => tasksIn(viewId, t(`${STRINGS_BASE}.view-${region.key}`), region)),
    [t],
  );

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <MessageBar intent="info">
            <MessageBarBody>{t(`${STRINGS_BASE}.${signedIn ? 'moves-saved' : 'sign-in-to-add'}`)}</MessageBarBody>
          </MessageBar>

          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-lanes`)} />
            <Item text={t(`${STRINGS_BASE}.try-add`)} />
            <Item text={t(`${STRINGS_BASE}.try-move`)} />
            <Item text={t(`${STRINGS_BASE}.try-close`)} />
            <Item text={t(`${STRINGS_BASE}.try-limits`)} />
            <Item text={t(`${STRINGS_BASE}.try-load-more`)} />
            <Item text={t(`${STRINGS_BASE}.try-collapse`)} />
            <Item text={t(`${STRINGS_BASE}.try-regions`)} />
            <Item text={t(`${STRINGS_BASE}.try-filter`)} />
            <Item text={t(`${STRINGS_BASE}.try-link`)} />
          </ul>
        </>
      }
    >
      <KanbanBoard
        defaultViewId={ALL_TASKS_VIEW_ID}
        customViewDefinitions={views}
        laneColumn="statuscode"
        lanes={STATUS_REASONS}
        laneLimits={LANE_LIMITS}
        editable
        allowFiltering
        persistedStateQueryParameter="boardState"
        height="760px"
      />
    </DemoShowcaseLayout>
  );
}
